export const en = {
  meta: { title: "Commission {artist} | StudioComm", description: "Request a commission from {artist}: pick options, add references, get an estimate. You only pay after the artist quotes." },
  steps: { type: "Type", scene: "Scene", characters: "Characters", contact: "Contact", review: "Terms" },
  header: { commissionWith: "Commission", changeArtist: "Change artist", browseArtists: "Browse artists" },
  banner: {
    waitlist: "{artist} is on a waitlist. You can still send a request, and they'll quote when a slot opens.",
    closed: "{artist} is closed right now. You can still send a request, and they'll review it when they reopen.",
  },
  notFound: { title: "We couldn't find that artist", body: "The link may be old or mistyped.", cta: "Browse artists" },
  loadError: { title: "Something went wrong loading this artist", retry: "Try again" },
  nav: { back: "Back", next: "Continue", send: "Send request", sending: "Sending…" },
  type: {
    question: "What are we making?",
    hint: "Pick the kind of piece. Prices are starting points, and {artist} confirms in the quote.",
    countQuestion: "How many characters?",
    countHint: "Each character gets its own card later.",
    sameQuestion: "Do they all have the same body type?",
    sameYes: "Yes", sameNo: "No",
    perCharacter: "Character {n}",
    from: "from", ask: "Ask",
    noTypes: "{artist} hasn't listed prices yet. Describe what you want and they'll quote.",
  },
  scene: {
    question: "Set the scene",
    background: "Background",
    bg: {
      none: { title: "None", body: "Transparent or a plain color." },
      simple: { title: "Simple", body: "{artist} picks a simple backdrop that fits your piece, like colors, a gradient or soft shapes. No description needed." },
      complex: { title: "Complex", body: "A full scene you describe. You'll add scene references in the next step." },
    },
    usage: "What's it for?",
    use: { personal: "Personal", commercial: "Commercial" },
    usageInfo: "What's the difference?",
    usageTip: {
      personal: "Just for you, like a profile picture, a print for your wall or a gift.",
      commercial: "Anything that earns money or promotes something, like streaming, VTubing, merch, ads or a book cover.",
    },
    usageHint: "Commercial use usually adds a fee. It's already in your estimate.",
    rating: "Content rating",
    ratings: { sfw: "SFW", suggestive: "Suggestive", nsfw: "NSFW (18+)" },
    deadline: "Do you have a deadline?",
    noRush: "No rush",
    byDate: "Set a deadline",
    deadlineInfo: "How deadlines work",
    deadlineTip: "{artist} usually takes about {time}.",
    deadlineTipRush: "If you need it in less than 5 days, a rush fee is added to your estimate.",
    deliveryTime: { one: "1 day", many: "{days} days", fallback: "two weeks" },
    rushNote: "That's less than 5 days away, so a rush fee is in your estimate.",
    addons: "Extras",
  },
  characters: {
    question: "Show us your characters!",
    hint: "Pictures, words or both. The more you share, the fewer questions {artist} has to ask.",
    card: "Character {n}",
    name: "Name (optional)",
    blocks: {
      look: { title: "What do they look like?", placeholder: "Ref sheets, outfit, colors, and details {artist} must get right, like scars, eye color or accessories." },
      action: { title: "What are they doing?", placeholder: "Pose, expression, mood or anything you picture. Not sure? Leave it to {artist}." },
      extra: { title: "Anything else?", placeholder: "Props, a specific outfit, things to avoid…" },
    },
    dropRefs: "Drop images or tap to upload",
    scene: "The scene",
    sceneDescription: "Describe the place",
    scenePlaceholder: "e.g. cozy café at night, rain outside",
    notes: "Anything else about the whole piece?",
    notesPlaceholder: "Optional: colors, mood, things to avoid…",
  },
  upload: {
    uploading: "Uploading…", retry: "Retry", remove: "Remove",
    tooBig: "{n} file(s) skipped. Images only, up to 10MB each.",
    tooMany: "Only {n} more image(s) fit here.",
    failed: "Upload failed",
  },
  contact: {
    question: "How can {artist} reach you?",
    name: "Your name", email: "Email", emailHint: "Your private tracking link goes here.",
    discord: "Discord (optional)",
    posting: "Can {artist} post the finished piece?",
    post: { yes: "Yes", delayed: "After a while", no: "Keep it private" },
    errors: { name: "Tell us your name", email: "Please enter a valid email" },
  },
  review: {
    question: "Quick look & terms",
    edit: "Edit",
    studioTerms: "StudioComm terms",
    artistTerms: "{artist}'s extra terms",
    accept: "I've read and accept these terms",
    termsUpdated: "The terms were just updated. Please take a quick look and accept again.",
    recap: {
      type: "Type", characters: "Characters", background: "Background", usage: "Usage", rating: "Rating",
      deadline: "Deadline", posting: "Posting", contact: "Contact", notes: "Notes", scene: "Scene", refs: "{n} images",
    },
  },
  errors: {
    validation: "A few things need a look. They're highlighted.",
    rateLimited: "Too many requests. Try again in {n}s.",
    network: "We couldn't send it, but your request is saved on this device.",
    retry: "Retry",
  },
  receipt: {
    title: "Estimate", total: "Estimated total", from: "from",
    ask: "artist will quote",
    note: "This is an estimate. {artist} confirms the final price in their quote.",
    empty: "Pick a type to see your estimate.",
  },
  checklist: {
    title: "What {artist} needs",
    progress: "{done} of {total}",
    items: {
      type: "Body type for every character", background: "Background", usage: "Usage", rating: "Content rating",
      something: "Something for every character", contact: "Contact + posting", terms: "Terms accepted",
      actions: "What each character is doing (recommended)",
    },
  },
  nudges: {
    faceRef: "Got a close-up of their face? Artists love those.",
    action: "Tell us what they're doing. It saves a round of questions.",
    multiRef: "Got a turnaround or outfit ref? Drop it here too.",
    complexScene: "One photo of a similar place helps a lot.",
    commercial: "Commercial use is already priced in, so no surprises later.",
    dismiss: "Got it",
  },
  success: {
    title: "Request sent!",
    body: "{artist} will review it and send you a quote. You only pay after you accept.",
    next: "What happens next",
    steps: ["{artist} sends you a quote", "You say yes (or ask for tweaks)", "You pay and we start working on it!"],
    trackingLabel: "Your private tracking link",
    copy: "Copy link", copied: "Copied ✓",
    emailed: "We also emailed this to you.",
    another: "Start another request", home: "Back to home",
  },
  tracking: {
    title: "Request #{ref}",
    loading: "Loading your request…",
    waiting: "{artist} is reviewing your request. You'll get an email when your quote is ready.",
    timeline: { requested: "Requested", quoted: "Quoted", accepted: "Accepted", paid: "Paid", in_progress: "In progress", delivered: "Delivered" },
    estimateLabel: "Estimate",
    notFoundTitle: "This link doesn't work anymore",
    notFoundBody: "It may have expired or been replaced by a newer one. We can email you fresh links.",
    resendLabel: "Your email",
    resendCta: "Email me my links",
    resendDone: "If that email has requests with us, fresh links are on their way.",
  },

  fees: {
    background_simple: "Simple background", background_complex: "Complex background",
    extra_character: "Extra character · {type}", complex_design: "Complex design",
    nsfw_content: "NSFW / suggestive", commercial_use: "Commercial use", rush_order: "Rush order",
  },
};
